/**
 * Discovery list state persistence.
 *
 * Tab-scoped, time-bounded sessionStorage snapshot of the `/discovery`
 * (PublicDashboardV2) list state. Snapshots are written on click into a
 * campaign detail row and read synchronously on the next mount of the
 * discovery page so that filters, search, view mode, pagination, and
 * scroll position are restored without a flash of default state.
 *
 * This module is a leaf utility: it depends only on the browser
 * `sessionStorage` API. No React, Apollo, i18n, or other project
 * imports.
 */

const STORAGE_KEY = 'mg.discovery.listState.v1';
const EXPIRY_MS = 5 * 60 * 1000; // 5 minutes
const SCHEMA_VERSION = 1;

/**
 * Public snapshot shape. The on-disk envelope adds `v` (schema version)
 * and `ts` (millisecond timestamp) fields, both internal to this
 * module.
 */
export interface DiscoveryListSnapshot {
  searchQuery: string;
  selectedCategory: string;
  selectedChannels: string[];
  /** Lowercase ISO-3166 alpha-2 country codes (kr / us / jp / …). */
  selectedCountries: string[];
  recruitStatus: 'all' | 'open' | 'closed';
  rewardFilter: 'all' | 'with' | 'without';
  viewMode: 'grid' | 'list';
  currentPage: number;
  scrollY: number;
}

const RECRUIT_STATUS_VALUES = ['all', 'open', 'closed'] as const;
const REWARD_FILTER_VALUES = ['all', 'with', 'without'] as const;
const VIEW_MODE_VALUES = ['grid', 'list'] as const;

function debugDiscard(reason: string): void {
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('[discoveryListState] discarding invalid snapshot', reason);
  }
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

/**
 * Persist the snapshot under the `mg.discovery.listState.v1` key with a
 * `{ v, ts, ...snapshot }` envelope. Silent on any sessionStorage error.
 */
export function saveDiscoveryListState(snapshot: DiscoveryListSnapshot): void {
  try {
    const envelope = {
      v: SCHEMA_VERSION,
      ts: Date.now(),
      ...snapshot,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
  } catch {
    /* sessionStorage unavailable, quota exceeded, etc. — silent */
  }
}

/**
 * Read and validate the snapshot. Returns `null` for missing, expired,
 * or corrupt entries. Never throws.
 */
export function loadDiscoveryListState(): DiscoveryListSnapshot | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }

  if (raw == null) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    debugDiscard('json parse failed');
    return null;
  }

  if (!parsed || typeof parsed !== 'object') {
    debugDiscard('envelope not an object');
    return null;
  }

  const envelope = parsed as Record<string, unknown>;

  if (envelope.v !== SCHEMA_VERSION) {
    debugDiscard(`schema version mismatch: ${String(envelope.v)}`);
    return null;
  }

  if (!isFiniteNumber(envelope.ts)) {
    debugDiscard('ts missing or non-numeric');
    return null;
  }

  if (Date.now() - envelope.ts > EXPIRY_MS) {
    debugDiscard('snapshot expired');
    return null;
  }

  if (typeof envelope.searchQuery !== 'string') {
    debugDiscard('searchQuery not a string');
    return null;
  }

  if (typeof envelope.selectedCategory !== 'string') {
    debugDiscard('selectedCategory not a string');
    return null;
  }

  if (!isStringArray(envelope.selectedChannels)) {
    debugDiscard('selectedChannels not a string array');
    return null;
  }

  // Backward-compatible: snapshots written before the country filter
  // existed won't have `selectedCountries`. Treat a missing value as an
  // empty selection rather than discarding the whole snapshot.
  if (
    envelope.selectedCountries !== undefined &&
    !isStringArray(envelope.selectedCountries)
  ) {
    debugDiscard('selectedCountries not a string array');
    return null;
  }

  if (
    typeof envelope.recruitStatus !== 'string' ||
    !RECRUIT_STATUS_VALUES.includes(envelope.recruitStatus as (typeof RECRUIT_STATUS_VALUES)[number])
  ) {
    debugDiscard(`recruitStatus out of domain: ${String(envelope.recruitStatus)}`);
    return null;
  }

  if (
    typeof envelope.rewardFilter !== 'string' ||
    !REWARD_FILTER_VALUES.includes(envelope.rewardFilter as (typeof REWARD_FILTER_VALUES)[number])
  ) {
    debugDiscard(`rewardFilter out of domain: ${String(envelope.rewardFilter)}`);
    return null;
  }

  if (
    typeof envelope.viewMode !== 'string' ||
    !VIEW_MODE_VALUES.includes(envelope.viewMode as (typeof VIEW_MODE_VALUES)[number])
  ) {
    debugDiscard(`viewMode out of domain: ${String(envelope.viewMode)}`);
    return null;
  }

  if (
    !isFiniteNumber(envelope.currentPage) ||
    !Number.isInteger(envelope.currentPage) ||
    envelope.currentPage < 1
  ) {
    debugDiscard(`currentPage not a positive integer: ${String(envelope.currentPage)}`);
    return null;
  }

  if (!isFiniteNumber(envelope.scrollY)) {
    debugDiscard('scrollY not a finite number');
    return null;
  }

  return {
    searchQuery: envelope.searchQuery,
    selectedCategory: envelope.selectedCategory,
    selectedChannels: envelope.selectedChannels,
    selectedCountries: isStringArray(envelope.selectedCountries)
      ? envelope.selectedCountries
      : [],
    recruitStatus: envelope.recruitStatus as DiscoveryListSnapshot['recruitStatus'],
    rewardFilter: envelope.rewardFilter as DiscoveryListSnapshot['rewardFilter'],
    viewMode: envelope.viewMode as DiscoveryListSnapshot['viewMode'],
    currentPage: envelope.currentPage,
    scrollY: envelope.scrollY,
  };
}

/**
 * Drop the snapshot. Silent on any sessionStorage error.
 */
export function clearDiscoveryListState(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* sessionStorage unavailable — silent */
  }
}
